/*
 * Floating feedback text — rises and fades over its lifetime.
 * Used for "+1 fish ✨", "it got away…", etc.
 */

import { Container, Text } from 'pixi.js';
import type { FloatText } from '@/state/types';

export interface FloatTextLayer {
  container: Container;
  spawn(text: string, x: number, y: number, color: string): void;
  update(dt: number): void;
}

export function createFloatTextLayer(): FloatTextLayer {
  const root = new Container();
  const live: Array<{ data: FloatText; node: Text }> = [];

  function spawn(text: string, x: number, y: number, color: string): void {
    const node = new Text({
      text,
      style: {
        fontFamily: 'Quicksand, sans-serif',
        fontSize: 13,
        fontWeight: '700',
        fill: parseInt(color.replace('#', ''), 16),
        dropShadow: { color: 0x000000, alpha: 0.6, distance: 1, blur: 0, angle: Math.PI / 4 },
      },
    });
    node.anchor.set(0.5, 0.5);
    node.position.set(x, y);
    root.addChild(node);

    live.push({
      data: { text, x, y, color, life: 1.6 },
      node,
    });
  }

  function update(dt: number): void {
    for (let i = live.length - 1; i >= 0; i--) {
      const entry = live[i]!;
      entry.data.life -= dt;
      entry.data.y -= 18 * dt;
      entry.node.position.y = entry.data.y;
      entry.node.alpha = Math.min(1, entry.data.life / 0.4);
      if (entry.data.life <= 0) {
        root.removeChild(entry.node);
        entry.node.destroy();
        live.splice(i, 1);
      }
    }
  }

  return { container: root, spawn, update };
}
